import { writable } from 'svelte/store';

// A tab can consent only while all its feature guards and mutation boundaries
// are idle. The coordinator exchanges no credentials, drafts or customer data.
export const updateState = writable({ available: false, busy: false, frozen: false, failed: false });
const guards = new Set();
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
let registration, prepared = null, mutations = 0, requesting = false;
let snapshot = { available: false, busy: false, frozen: false, failed: false };
const publish = changes => { snapshot = { ...snapshot, ...changes }; updateState.set(snapshot); };
export const updateFrozen = () => !!prepared;

export function registerUpdateGuard(check) {
  guards.add(check);
  return () => guards.delete(check);
}

export function beginInboxMutation() {
  if (prepared) throw Object.assign(new Error('Update in progress'), { code: 'inbox_update_in_progress' });
  mutations += 1;
  let finished = false;
  return () => { if (!finished) { finished = true; mutations -= 1; } };
}

function ready() {
  try {
    return navigator.onLine && !location.search && /^#\/inbox(?:\/[0-9a-f-]{36})?$/.test(location.hash) &&
      guards.size > 0 && mutations === 0 && [...guards].every(check => check() === true);
  } catch { return false; }
}

function release(failed = false) {
  clearTimeout(prepared?.timer);
  prepared = null;
  publish({ frozen: false, busy: requesting, failed });
}

function refresh() {
  publish({ available: !!registration?.waiting && registration.waiting.state === 'installed' });
}

function sameCredential() {
  try { return !!prepared?.token && prepared.token === localStorage.getItem('jwtToken'); }
  catch { return false; }
}

export function willReloadAfterWorkerChange() {
  return !!(prepared?.committed && navigator.serviceWorker.controller === prepared.worker &&
    prepared.deadline > Date.now() && sameCredential() && ready());
}

export function installInboxUpdates() {
  if (!navigator.serviceWorker) return;
  navigator.serviceWorker.ready.then(value => {
    if (value.scope !== location.origin + '/' || value.active?.scriptURL !== location.origin + '/service-worker.js') return;
    registration = value;
    const installing = () => {
      const candidate = value.installing;
      if (candidate) candidate.addEventListener('statechange', refresh);
      refresh();
    };
    value.addEventListener('updatefound', installing);
    installing();
  }).catch(() => {});
  navigator.serviceWorker.addEventListener('message', async event => {
    const data = event.data, port = event.ports?.[0];
    if (data?.type !== 'tutory:update-phase' || data.version !== 1 || !UUID.test(data.id) ||
        !['prepare', 'commit', 'cancel'].includes(data.phase)) return;
    try {
      if (event.source?.scriptURL !== location.origin + '/service-worker.js') return;
      const current = await navigator.serviceWorker.getRegistration('/');
      if (event.source !== current?.waiting && event.source !== prepared?.worker) return;
      if (data.phase === 'cancel') {
        if (prepared?.id === data.id && prepared.worker === event.source) release(true);
        return;
      }
      if (!port) return;
      let allowed = false;
      if (data.phase === 'prepare' && !prepared && ready()) {
        prepared = { id: data.id, worker: event.source, committed: false,
          deadline: Date.now() + 12000, token: localStorage.getItem('jwtToken'),
          timer: setTimeout(() => release(true), 12000) };
        publish({ frozen: true, busy: true, failed: false });
        allowed = true;
      } else if (data.phase === 'commit' && prepared?.id === data.id &&
          prepared.worker === event.source && prepared.deadline > Date.now() &&
          sameCredential() && ready()) {
        prepared.committed = true;
        allowed = true;
      }
      port.postMessage({ version: 1, id: data.id, phase: data.phase, ready: allowed });
    } catch { /* No response is a veto, never implicit consent. */ }
    finally { port?.close(); }
  });
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (willReloadAfterWorkerChange()) {
      clearTimeout(prepared.timer);
      // Only a currently frozen, consenting tab reloads. Activation alone
      // never authorizes reloading a new, old, unknown or interrupted client.
      location.reload();
    } else if (prepared) release(true);
    refresh();
  });
  window.addEventListener('pagehide', () => { if (prepared) release(true); });
  window.addEventListener('pageshow', refresh);
}

export async function requestInboxUpdate() {
  if (requesting || prepared) return;
  requesting = true;
  publish({ busy: true, failed: false });
  let port, timer, id;
  try {
    if (!ready() || !registration?.waiting) throw new Error('Update blocked');
    id = crypto.randomUUID();
    const worker = registration.waiting, channel = new MessageChannel();
    port = channel.port1;
    const result = await new Promise((resolve, reject) => {
      timer = setTimeout(() => reject(new Error('Update not confirmed')), 9000);
      port.onmessage = event => {
        if (event.data?.version === 1 && event.data.id === id) resolve(event.data);
      };
      worker.postMessage({ type: 'tutory:update-request', version: 1, id }, [channel.port2]);
    });
    if (result.ok !== true) throw new Error('Update blocked');
  } catch {
    // A concurrent request in another tab may already own our freeze. A
    // rejected local request is not authority to cancel that other handshake.
    if (!prepared || prepared.id === id) release(true);
  } finally {
    clearTimeout(timer); port?.close(); requesting = false;
    publish({ busy: !!prepared }); refresh();
  }
}
